import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { API_URL } from '../../socket';
import { resolveMoveGifUrl } from '../../utils/moveGifUrl';
import { MOVE_GIF_MAP } from '../../utils/moveGifMap';

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: var(--space-4) 0 var(--space-4);
  padding: 10px 12px;
  background: rgba(8, 7, 10, 0.45);
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
`;

const Search = styled.input`
  flex: 1 1 260px;
  min-width: 220px;
  padding: 10px 12px;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-cream-100);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  outline: none;
  clip-path: var(--clip-button);
  &:focus {
    border-color: var(--color-amber-500);
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: var(--color-cream-400);
  white-space: nowrap;
`;

const Msg = styled.p`
  margin: 0 0 var(--space-3);
  font-size: 13px;
  color: ${(p) => (p.$err ? 'var(--color-red-400)' : 'var(--color-cream-300)')};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid var(--color-ink-700);
  clip-path: var(--clip-card);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border-bottom: 1px solid var(--color-ink-700);
    padding: 8px;
    vertical-align: top;
    text-align: left;
  }
  th {
    font-family: var(--font-heading);
    font-size: 10px;
    letter-spacing: var(--tracking-widest);
    text-transform: uppercase;
    color: var(--color-amber-500);
    background: rgba(8, 7, 10, 0.9);
    position: sticky;
    top: 0;
    z-index: 1;
  }
  td {
    background: rgba(8, 7, 10, 0.35);
  }

  tbody tr:nth-child(2n) td {
    background: rgba(8, 7, 10, 0.28);
  }
`;

const SmallInput = styled.input`
  width: 100%;
  max-width: 120px;
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-cream-100);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-600);
  outline: none;
  &:focus {
    border-color: var(--color-amber-500);
  }
`;

const NameInput = styled(SmallInput)`
  max-width: 200px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  padding: 10px 12px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-cream-100);
  background: var(--color-ink-900);
  border: 1px solid var(--color-ink-700);
  resize: vertical;
  outline: none;
  &:focus {
    border-color: var(--color-amber-500);
  }
`;

const Btn = styled.button`
  padding: 10px 18px;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  border: 1px solid var(--color-amber-500);
  background: var(--color-amber-500);
  color: var(--color-ink-950);
  cursor: pointer;
  clip-path: var(--clip-button);
  &:hover {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled(Btn)`
  background: transparent;
  color: var(--color-cream-200);
  border-color: var(--color-ink-600);
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-cream-300);
`;

const GifPreview = styled.img`
  display: block;
  width: 48px;
  height: 48px;
  object-fit: contain;
  background: var(--color-ink-950);
  border: 1px solid var(--color-ink-600);
  margin-bottom: 6px;
`;

const FilePickLabel = styled.label`
  display: inline-block;
  padding: 6px 10px;
  font-family: var(--font-heading);
  font-size: 9px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-200);
  border: 1px solid var(--color-ink-600);
  cursor: pointer;
  clip-path: var(--clip-button);
  margin-right: 6px;
  &:hover {
    border-color: var(--color-amber-500);
    color: var(--color-amber-500);
  }
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

function effectsToString(effects) {
  if (effects == null) return '[]';
  try {
    return JSON.stringify(effects, null, 2);
  } catch {
    return '[]';
  }
}

/**
 * @param {object} props
 * @param {string} props.token — Bearer from POST /api/admin/login
 * @param {() => void} [props.onSessionExpired] — called on 401 from admin move APIs
 */
export function AdminMovesEditor({ token, onSessionExpired }) {
  const [moves, setMoves] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState('');

  const jsonHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const loadMoves = useCallback(async () => {
    if (!token) return;
    setErr(null);
    setMsg(null);
    setLoading(true);
    try {
      const mr = await fetch(`${API_URL}/api/moves`);
      if (!mr.ok) throw new Error('Failed to load moves');
      const list = await mr.json();
      setMoves(Array.isArray(list) ? list : []);
      const map = {};
      for (const m of list) {
        map[m.moveId] = {
          name: m.name || '',
          power: m.power ?? 0,
          accuracy: m.accuracy === true ? 'true' : String(m.accuracy ?? 100),
          limit: m.limit ?? 10,
          priority: m.priority ?? 0,
          effectsJson: effectsToString(m.effects),
        };
      }
      setRows(map);
      setMsg('Edit rows and save. Move limits live in the database.');
    } catch (e) {
      setErr(e.message || 'Failed to load moves');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMoves();
  }, [loadMoves]);

  const visibleMoves = moves.filter((m) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = `${m.moveId || ''} ${m.name || ''}`.toLowerCase();
    return hay.includes(q);
  });

  const handleAdminError = (status) => {
    if (status === 401 && onSessionExpired) onSessionExpired();
  };

  const saveRow = async (moveId) => {
    setErr(null);
    setMsg(null);
    const row = rows[moveId];
    if (!row) return;
    let effects;
    try {
      effects = row.effectsJson.trim() ? JSON.parse(row.effectsJson) : [];
      if (!Array.isArray(effects)) {
        setErr(`Effects for ${moveId} must be a JSON array.`);
        return;
      }
    } catch {
      setErr(`Invalid JSON in effects for ${moveId}.`);
      return;
    }
    let accuracy = row.accuracy;
    if (accuracy === 'true' || accuracy === true) {
      accuracy = true;
    } else {
      accuracy = Number(accuracy);
      if (!Number.isFinite(accuracy)) {
        setErr(`Invalid accuracy for ${moveId}`);
        return;
      }
    }
    const body = {
      name: row.name,
      power: Number(row.power),
      accuracy,
      limit: Number(row.limit),
      priority: Number(row.priority) || 0,
      effects,
    };
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/moves/${encodeURIComponent(moveId)}`, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        handleAdminError(r.status);
        setErr(data.error || `Save failed (${r.status})`);
        return;
      }
      setMsg(`Saved ${moveId}.`);
      setMoves((prev) => prev.map((m) => (m.moveId === moveId ? { ...m, ...data } : m)));
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (moveId, field, value) => {
    setRows((prev) => ({
      ...prev,
      [moveId]: { ...prev[moveId], [field]: value },
    }));
  };

  const uploadGif = async (moveId, file) => {
    setErr(null);
    setMsg(null);
    const fd = new FormData();
    fd.append('gif', file);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/moves/${encodeURIComponent(moveId)}/gif`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        handleAdminError(r.status);
        setErr(data.error || `GIF upload failed (${r.status})`);
        return;
      }
      setMsg(`GIF saved for ${moveId}.`);
      if (data.move) {
        setMoves((prev) => prev.map((m) => (m.moveId === moveId ? { ...m, ...data.move } : m)));
      }
    } catch (e) {
      setErr(e.message || 'GIF upload failed');
    } finally {
      setLoading(false);
    }
  };

  const removeHostedGif = async (moveId) => {
    setErr(null);
    setMsg(null);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/moves/${encodeURIComponent(moveId)}/gif`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        handleAdminError(r.status);
        setErr(data.error || `Remove failed (${r.status})`);
        return;
      }
      setMsg(`Hosted GIF cleared for ${moveId}.`);
      if (data.move) {
        setMoves((prev) => prev.map((m) => (m.moveId === moveId ? { ...m, ...data.move } : m)));
      }
    } catch (e) {
      setErr(e.message || 'Remove failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Msg $err={!!err}>{err}</Msg>
      {msg && !err && <Msg>{msg}</Msg>}

      <Msg>
        <strong>GIFs:</strong> <Mono>.gif</Mono> per move (~5MB). Stored under{' '}
        <Mono>/uploads/moves/&lt;moveId&gt;.gif</Mono> on the API. For a split client/API host, set{' '}
        <Mono>PUBLIC_API_URL</Mono> on the server.
      </Msg>

      <Controls>
        <Search
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search moves by name or moveId…"
          aria-label="Search moves"
        />
        <Hint>
          Showing <strong>{visibleMoves.length}</strong> / {moves.length}
        </Hint>
        <GhostBtn type="button" disabled={loading} onClick={loadMoves}>
          Reload
        </GhostBtn>
      </Controls>

      {visibleMoves.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>moveId</th>
                <th>Name</th>
                <th>Pwr</th>
                <th>Acc</th>
                <th>Limit</th>
                <th>Prio</th>
                <th>GIF</th>
                <th>Effects (JSON array)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleMoves.map((m) => {
                const r = rows[m.moveId];
                if (!r) return null;
                return (
                  <tr key={m.moveId}>
                    <td>
                      <Mono>{m.moveId}</Mono>
                    </td>
                    <td>
                      <NameInput
                        value={r.name}
                        onChange={(e) => updateRow(m.moveId, 'name', e.target.value)}
                      />
                    </td>
                    <td>
                      <SmallInput
                        type="number"
                        value={r.power}
                        onChange={(e) => updateRow(m.moveId, 'power', e.target.value)}
                      />
                    </td>
                    <td>
                      <SmallInput
                        value={r.accuracy}
                        onChange={(e) => updateRow(m.moveId, 'accuracy', e.target.value)}
                        title="Number 0–100, or type true for always hit"
                      />
                    </td>
                    <td>
                      <SmallInput
                        type="number"
                        value={r.limit}
                        onChange={(e) => updateRow(m.moveId, 'limit', e.target.value)}
                      />
                    </td>
                    <td>
                      <SmallInput
                        type="number"
                        value={r.priority}
                        onChange={(e) => updateRow(m.moveId, 'priority', e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <GifPreview
                        src={resolveMoveGifUrl(m, MOVE_GIF_MAP)}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.visibility = 'hidden';
                        }}
                      />
                      <FilePickLabel>
                        Upload
                        <input
                          type="file"
                          accept="image/gif,.gif"
                          disabled={loading}
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            if (f) uploadGif(m.moveId, f);
                            e.target.value = '';
                          }}
                        />
                      </FilePickLabel>
                      {m.gifUrl && (
                        <GhostBtn
                          type="button"
                          disabled={loading}
                          onClick={() => removeHostedGif(m.moveId)}
                          style={{ marginTop: 6, padding: '6px 10px', fontSize: 9 }}
                        >
                          Clear hosted
                        </GhostBtn>
                      )}
                      {m.gifUrl && (
                        <div style={{ marginTop: 6, maxWidth: 180, wordBreak: 'break-all' }}>
                          <Mono style={{ fontSize: 9 }}>{m.gifUrl}</Mono>
                        </div>
                      )}
                    </td>
                    <td style={{ minWidth: 280 }}>
                      <TextArea
                        value={r.effectsJson}
                        onChange={(e) => updateRow(m.moveId, 'effectsJson', e.target.value)}
                        spellCheck={false}
                      />
                    </td>
                    <td>
                      <RowActions>
                        <Btn type="button" disabled={loading} onClick={() => saveRow(m.moveId)}>
                          Save
                        </Btn>
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {!loading && moves.length === 0 && (
        <Msg>No moves from /api/moves — seed the database first.</Msg>
      )}
      {!loading && moves.length > 0 && visibleMoves.length === 0 && (
        <Msg>No moves match that search.</Msg>
      )}
    </>
  );
}
